import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { GenerationSiteFormFields } from '~/pages/AdminPlantUnitMaster/components/GenerationSiteFormFields'
import { useCreateGenerationSite } from '~/pages/AdminPlantUnitMaster/hooks/useCreateGenerationSite'
import {
  GENERATION_SITE_SAVE_ERROR_KEYS,
  GENERATION_SITE_UNKNOWN_ERROR_KEY,
  resolveGenerationSiteSaveError,
} from '~/pages/AdminPlantUnitMaster/models/serverError'
import {
  type GenerationSiteFormInput,
  type GenerationSiteFormValues,
  buildGenerationSiteFormSchema,
  toOptionalText,
} from '~/pages/AdminPlantUnitMaster/models/siteForm'

type Props = {
  isOpen: boolean
  onClose: () => void
  /** 新規のエリアの既定。一覧をエリアで絞っていればそのエリア、「すべて」(UNSPECIFIED) なら東京。 */
  defaultAreaCode: AreaCode
}

/** 発電所 (サイト) の登録ダイアログ。 */
export const CreateGenerationSiteModal = ({
  isOpen,
  onClose,
  defaultAreaCode,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreateGenerationSite()

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    isDirty,
    isSubmitting,
  } = useCrudModalForm<GenerationSiteFormInput, GenerationSiteFormValues>({
    schema: buildGenerationSiteFormSchema(t),
    defaultValues: {
      siteName: '',
      siteNameAbbr: '',
      areaCode:
        defaultAreaCode === AreaCode.UNSPECIFIED
          ? AreaCode.TOKYO
          : defaultAreaCode,
      note: '',
    },
    onSubmit: async (values) => {
      await mutateAsync({
        siteName: values.siteName,
        siteNameAbbr: toOptionalText(values.siteNameAbbr),
        areaCode: values.areaCode,
        note: toOptionalText(values.note),
      })
    },
    onClose,
    customErrorKeys: GENERATION_SITE_SAVE_ERROR_KEYS,
    unknownErrorKey: GENERATION_SITE_UNKNOWN_ERROR_KEY,
    resolveDetailedError: resolveGenerationSiteSaveError,
  })

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending || isSubmitting}
      onClose={handleClose}
      serverError={serverError}
      size="md"
      title={t('page:adminPlantUnitMaster.site.createTitle')}
    >
      <GenerationSiteFormFields
        control={control}
        disabled={isPending || isSubmitting}
        errors={errors}
      />
    </CrudModalLayout>
  )
}
